import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { RESTAPIError } from '../../Common/API'
import { StatusCodes } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { RequestMapperOptions } from '../../Common/Types/Types'
import { APIListData, JSONResponse, RESTOptions, ServerError } from '../../../NonReact/common/Types/Types'

/**
 * It takes a URL and an options object, and returns a promise that resolves to the response object
 * @param {URLConstructor} url - URLConstructor - This is the URL that you want to make the request to.
 * @param {RESTOptions} options - {
 * @returns A promise that resolves to the response from the requestJSON function.
 */
export const requestMapper = <T, U extends ServerError>(
    url: URLConstructor,
    options: RESTOptions<object>,
    mapperOptions?: RequestMapperOptions<U>
): Promise<T> =>
    new Promise((resolve, reject): void => {
        requestJSON<APIListData<ServerError>>(url, options)
            .then((response: JSONResponse<APIListData<ServerError> | T>): void => {
                const responseAsError: APIListData<ServerError> = response as APIListData<ServerError>

                try {
                    if (
                        responseAsError?.Data?.Items &&
                        responseAsError.Data.Items.some((item: ServerError): boolean => isVarNotEmpty(item.ErrorCode))
                    ) {
                        const newResponse: Error = new RESTAPIError(
                            mapperOptions?.overrideStatusCode ?? StatusCodes.INTERNAL_SERVER_ERROR,
                            null,
                            mapperOptions?.errorMessageMapper
                                ? mapperOptions.errorMessageMapper(responseAsError.Data.Items as U[]).serverError
                                : responseAsError.Data.Items.join(', '),
                            null
                        ) as Error

                        const containsSuccessfulCodes: boolean = responseAsError.Data.Items.some(
                            (item: ServerError): boolean => IsVarEmpty(item.ErrorCode)
                        )

                        reject(
                            containsSuccessfulCodes
                                ? {
                                      error: newResponse,
                                      successes: mapperOptions?.errorMessageMapper
                                          ? mapperOptions?.errorMessageMapper(responseAsError.Data.Items as U[])
                                                .successfulCodes
                                          : responseAsError.Data.Items.join(', ')
                                  }
                                : newResponse
                        )
                    } else {
                        resolve(response as T)
                    }
                } catch (err) {
                    resolve(response as T)
                }
            })
            .catch(reject)
    })
